import { useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, RefreshCw, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { PageHeader, Panel, Th, Td, btn, field } from "@/components/ui-kit";

const Pick = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`h-9 w-44 ${field}`} data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

const csv = (rows) => {
  const cols = ["at_dmy", "direction", "partner_name", "from_id", "to_id", "ip", "document", "http", "mdn", "ok", "error", "message_id"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const blob = new Blob([[cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "AS2_connections_log.csv";
  a.click();
};

const Cert = ({ label, c, tone = "text-slate-300" }) => (
  <div className="mt-1"><span className="text-slate-500">{label}: </span><span className={tone}>{c.subject || "(certificate not included)"}{c.serial && ` · serial ${c.serial}`}</span>
    {c.sha1 && <div className="break-all font-mono text-[10px] text-slate-400">SHA-1 {c.sha1}</div>}</div>
);

const CertCheck = ({ r }) => {
  const k = r.cert_check;
  if (k) {
    return (
      <div className={`mt-2 rounded-sm border px-2 py-1.5 ${k.match ? "border-amber-500/40" : "border-rose-500/40 bg-rose-500/5"}`} data-testid={`conn-cert-check-${r.id}`}>
        <div className={`font-medium ${k.match ? "text-amber-300" : "text-rose-300"}`}>{k.match ? "Same certificate - the content changed in transit or the digest differs" : "Certificate mismatch"}</div>
        {k.signer ? <Cert label="Partner signed with" c={k.signer} tone="text-rose-200" /> : <div className="mt-1 text-slate-500">Signer certificate could not be read</div>}
        {k.ours.map((c) => <Cert key={c.label} label={`We hold (${c.label})`} c={c} tone={k.signer?.sha1 === c.sha1 ? "text-emerald-300" : "text-slate-300"} />)}
      </div>
    );
  }
  if (r.ep_cert) {
    return (
      <div className="mt-2 rounded-sm border border-amber-500/40 px-2 py-1.5" data-testid={`conn-ep-cert-${r.id}`}>
        <div className="font-medium text-amber-300">Partner could not verify us - check they have loaded this certificate</div>
        <Cert label="We signed with (EP)" c={r.ep_cert} />
      </div>
    );
  }
  return null;
};

const Row = ({ r }) => (
  <tr data-testid={`conn-row-${r.id}`}>
    <Td className="whitespace-nowrap font-mono text-xs">{r.at_dmy}</Td>
    <Td className="text-xs">{r.direction === "in" ? <span className="text-sky-300"><ArrowDownLeft className="mr-1 inline h-3.5 w-3.5" />In</span> : <span className="text-violet-300"><ArrowUpRight className="mr-1 inline h-3.5 w-3.5" />Out</span>}</Td>
    <Td className="text-xs">{r.partner_name}</Td>
    <Td className="font-mono text-xs"><div>{r.from_id || "-"} → {r.to_id || "-"}</div>{r.ip && <div className="text-slate-500">IP {r.ip}</div>}</Td>
    <Td className="text-xs">{r.document || "-"}{(r.http || r.mdn) && <div className="text-slate-500">{r.http && `HTTP ${r.http}`}{r.mdn && ` · MDN ${r.mdn}`}</div>}</Td>
    <Td className="max-w-[360px] text-xs"><div className={`font-medium ${r.ok ? "text-emerald-300" : "text-rose-300"}`}>{r.ok ? "OK" : "Failed"}</div>{r.error && <div className="break-words text-slate-400">{r.error}</div>}<CertCheck r={r} /></Td>
  </tr>
);

export default function Connections() {
  const [rows, setRows] = useState(null);
  const [partners, setPartners] = useState([]);
  const [f, setF] = useState({ partner: "all", direction: "all", status: "all", days: "30" });
  const load = () => {
    const p = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "all" ? "" : v]));
    setRows(null);
    api.get("/as2/connections", { params: p }).then((r) => setRows(r.data));
  };
  useEffect(() => { api.get("/partners").then((r) => setPartners(r.data.partners)); }, []);
  useEffect(() => { load(); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <div>
      <PageHeader title="AS2 connections log" subtitle="Every AS2 message received from and sent to trading partners, with the AS2 IDs, HTTP / MDN result and the exact error.">
        <Button variant="ghost" onClick={() => rows && csv(rows)} className={btn.ghost} data-testid="conn-export"><Download className="mr-1.5 h-4 w-4" /> Export CSV</Button>
        <Button onClick={load} className={btn.primary} data-testid="conn-refresh"><RefreshCw className="mr-1.5 h-4 w-4" /> Refresh</Button>
      </PageHeader>
      <div className="mb-4 flex flex-wrap gap-3">
        <Pick value={f.partner} onChange={set("partner")} testId="conn-filter-partner" options={[["all", "All partners"], ...partners.map((p) => [p.code, p.name])]} />
        <Pick value={f.direction} onChange={set("direction")} testId="conn-filter-direction" options={[["all", "In and out"], ["in", "Received"], ["out", "Sent"]]} />
        <Pick value={f.status} onChange={set("status")} testId="conn-filter-status" options={[["all", "All results"], ["ok", "OK only"], ["failed", "Failed only"]]} />
        <Pick value={f.days} onChange={set("days")} testId="conn-filter-days" options={[["1", "Last 24 hours"], ["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"]]} />
      </div>
      <Panel title={`Connections${rows ? ` (${rows.length})` : ""}`} testId="conn-log">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="border-b border-slate-800"><tr><Th>When</Th><Th>Way</Th><Th>Partner</Th><Th>AS2 from → to</Th><Th>Document</Th><Th>Result</Th></tr></thead>
          <tbody className="divide-y divide-slate-800/70">{(rows || []).map((r) => <Row key={`${r.direction}-${r.id}`} r={r} />)}</tbody>
        </table>
        {rows && !rows.length && <p className="px-5 py-4 text-xs text-slate-500" data-testid="conn-empty">No AS2 connections in this period.</p>}
        {!rows && <p className="px-5 py-4 text-xs text-slate-500">Loading…</p>}</div>
      </Panel>
    </div>
  );
}
